import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import { HomeFeedResponse } from './useHomeFeed';
import { OrderFeedEntry } from './useGetOrdersFeed';
import { OrderDetailResponse } from './useGetOrderDetail';
import { OrderDrawerResponse } from './useGetOrderDrawer';
import { invalidateOrder } from './invalidateOrder';

export interface CompleteOrderInput {
    orderId?: string;
    orderIds?: string[];
}

const completeOrder = async (data: CompleteOrderInput, sessionId: string | null) => {
    return axios.post(`${API_URL}/order/complete`, data, {
        headers: {
            Authorization: sessionId
        }
    });
};

export const useCompleteOrder = () => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: CompleteOrderInput) => completeOrder(data, sessionContext?.sessionId ?? null),
        onError: (error) => {
            console.error('Failed to complete order:', error);
        },
        onMutate: async (variables) => {
            if (variables.orderIds?.length || !variables.orderId) return;

            await queryClient.cancelQueries({ queryKey: ['/orders/home-feed-paginated'] });

            queryClient.setQueriesData<HomeFeedResponse>({ queryKey: ['/orders/home-feed-paginated'] }, (old) => {
                if (!old) return old;
                return {
                    ...old,
                    orders: old.orders.filter(order => order._id !== variables.orderId)
                };
            });

            queryClient.setQueriesData(
                { queryKey: ['/dashboard/orders-feed-paginated'] },
                (old: { orders: OrderFeedEntry[]; } | undefined) => {
                    if (!old || !old.orders) return old;
                    return {
                        ...old,
                        orders: old.orders.filter(order => order._id !== variables.orderId)
                    };
                }
            );

            queryClient.setQueryData(
                ['/order/detail', variables.orderId],
                (old: OrderDetailResponse | undefined): OrderDetailResponse | undefined => {
                    if (!old) return old;
                    return {
                        ...old,
                        completed: !old.completed,
                        archived: false
                    };
                }
            );

            queryClient.setQueryData<OrderDrawerResponse>(
                ['/order/drawer', variables.orderId],
                (old) => {
                    if (!old) return old;
                    return {
                        ...old,
                        completed: !old.completed,
                        archived: false
                    };
                }
            );
        },
        onSuccess: async (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['/orders/home-feed-paginated'] });

            if (!variables.orderId) return;

            await invalidateOrder(queryClient, variables.orderId);
        },
    });
};
